/**
 * Allowed users (email + role) whitelist for Executive Analytics.
 * Source of truth: server config file via GET/PATCH /api/allowed-emails (public/config/allowed_emails.json).
 * Empty list = allow all (for initial setup); non-empty = only listed users can sign in.
 */

import { DEFAULT_ROLE, VALID_ROLES } from '../config/roles';

export interface AllowedUser {
  email: string;
  role: string;
}

const API_URL = '/api/allowed-emails';

/**
 * Fetch the allowed users list from the server (email + role per user).
 */
export async function fetchAllowedUsers(): Promise<AllowedUser[]> {
  try {
    const res = await fetch(API_URL);
    const data = await res.json().catch(() => ({}));
    const list = Array.isArray(data?.users) ? data.users : [];
    return list
      .map((u: any) => {
        const email = typeof u?.email === 'string' ? u.email.trim().toLowerCase() : '';
        const role = typeof u?.role === 'string' ? u.role.trim().toLowerCase() : '';
        return { email, role: (role && VALID_ROLES.includes(role as any)) ? role : DEFAULT_ROLE };
      })
      .filter((u: AllowedUser) => u.email);
  } catch (_) {
    return [];
  }
}

/**
 * Legacy: Fetch allowed emails as flat array (for backward compatibility).
 * Prefer fetchAllowedUsers() for RBAC.
 */
export async function fetchAllowedEmails(): Promise<string[]> {
  const users = await fetchAllowedUsers();
  return users.map((u) => u.email);
}

/**
 * Find user by email in the allowed users list.
 */
export function getUserByEmail(email: string, users: AllowedUser[]): AllowedUser | null {
  if (!Array.isArray(users) || users.length === 0) return null;
  const normalized = String(email ?? '').trim().toLowerCase();
  if (!normalized) return null;
  return users.find((u) => u.email === normalized) || null;
}

/**
 * Check if an email is in the allowed list. Empty list = allow all.
 */
export function isEmailInAllowedList(email: string, users: AllowedUser[]): boolean {
  if (!Array.isArray(users) || users.length === 0) return true;
  const normalized = String(email ?? '').trim().toLowerCase();
  if (normalized === '') return false;
  return users.some((u) => u.email === normalized);
}

/**
 * Save the allowed users list to the server. Replaces the current list.
 * Admin only: server requires X-User-Email header with an email in admin_emails.json.
 */
export async function saveAllowedUsers(
  users: AllowedUser[],
  userEmail?: string
): Promise<{ ok: boolean; users?: AllowedUser[] }> {
  const list = (Array.isArray(users) ? users : [])
    .map((u: any) => {
      const email = typeof u?.email === 'string' ? u.email.trim().toLowerCase() : '';
      const role = typeof u?.role === 'string' ? u.role.trim().toLowerCase() : '';
      return { email, role: (role && VALID_ROLES.includes(role as any)) ? role : DEFAULT_ROLE };
    })
    .filter((u: AllowedUser) => u.email);
  const seen = new Set<string>();
  const unique = list.filter((u: AllowedUser) => {
    if (seen.has(u.email)) return false;
    seen.add(u.email);
    return true;
  });
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (userEmail && typeof userEmail === 'string') {
    headers['X-User-Email'] = userEmail.trim();
  }
  const res = await fetch(API_URL, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ users: unique })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Save failed (${res.status})`);
  return data;
}
